import { type IntlShape } from 'react-intl';

import { NounKeys, nouns, NOUNS_ORDER } from '../constants/nouns';
import {
	type PrimaryProductKey,
	type ProductKeys,
	ProductKeys as ProductKeysObject,
} from '../constants/product-keys';
import { type ProductConfig } from '../constants/products';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../constants/schemas/3p-config';

import { isProductConnected } from './connectors';
import { getProductOrder } from './get-product-order';
import { getProductsOrderByPrimaryProduct } from './get-products-order-by-primary-product';
import { getWorkspaceNameFromProduct } from './get-workspace-name-from-product';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getShouldShowNouns } from './get-should-show-nouns';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { filterProductsForNouns } from './filter-products-for-nouns';

export type AppOption = {
	value: ProductKeys | NounKeys;
	label: string;
	isSelected: boolean;
	isUnauthenticated?: boolean;
};

/**
 * Builds a sorted list of product options with resolved labels
 * (workspace name from 3P config, falling back to product displayName).
 */
export function buildProductOptions({
	filteredProducts,
	products,
	selectedProduct,
	thirdPartyConfigs,
	primaryProduct,
}: {
	filteredProducts: ProductKeys[];
	products: Record<string, ProductConfig>;
	selectedProduct: ProductKeys | undefined;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	primaryProduct?: PrimaryProductKey;
}): AppOption[] {
	const productsOrder = getProductsOrderByPrimaryProduct(primaryProduct);

	return filteredProducts
		.filter((key) => !!products[key])
		.sort((a, b) => getProductOrder(a, b, productsOrder))
		.map((key) => {
			const workspaceName = getWorkspaceNameFromProduct(key, thirdPartyConfigs);
			return {
				value: key,
				label: workspaceName || String(products[key].displayName),
				isSelected: key === selectedProduct,
			};
		});
}

/**
 * Builds a sorted list of noun options from availableNouns and (optionally) WebCrawler.
 *
 * @param selectedNounType - The currently selected noun type value (e.g. from the type query param).
 *   When Atlas/townsquare is the selected product, this is used to determine which noun is active.
 */
export function buildNounOptions({
	availableNouns,
	availableProducts,
	selectedProduct,
	selectedNounType,
	formatMessage,
}: {
	availableNouns: NounKeys[];
	availableProducts: ProductKeys[];
	selectedProduct: ProductKeys | undefined;
	selectedNounType?: string;
	formatMessage: IntlShape['formatMessage'];
}): AppOption[] {
	const isWebCrawlerAvailable = availableProducts.includes(ProductKeysObject.AtlassianWebCrawler);

	const nounsToDisplay = [
		...availableNouns.filter((noun) => noun !== NounKeys.AtlassianWebCrawler),
		...(isWebCrawlerAvailable
			? [ProductKeysObject.AtlassianWebCrawler as unknown as NounKeys]
			: []),
	].sort((a, b) => NOUNS_ORDER.indexOf(a) - NOUNS_ORDER.indexOf(b));

	const isAtlasSelected = selectedProduct === ProductKeysObject.Atlas;

	return nounsToDisplay
		.filter((noun) => !!nouns[noun as NounKeys])
		.map((noun) => ({
			value: noun as NounKeys,
			label: formatMessage(nouns[noun as NounKeys].displayNamePlural),
			isSelected:
				noun === NounKeys.AtlassianWebCrawler
					? selectedProduct === ProductKeysObject.AtlassianWebCrawler
					: isAtlasSelected && noun === selectedNounType,
		}));
}

/**
 * Returns the Atlassian Web Crawler website datasources that should be surfaced as
 * individual apps (one per website, e.g. "Atlassian Developer"), mirroring the
 * standard right-rail "fan-out" behaviour. Returns an empty array when the connector
 * does not opt into fan-out (`fanOutConnectorSources` is falsy) or has no datasources.
 *
 * The `webcrawler-fanout-ui-sidebar-enabled` feature gate is intentionally checked by
 * the caller (not here) so this stays a pure, easily-testable function. This is the
 * Lite-mode counterpart to the right rail's `createFanoutOptions`, which is what keeps
 * the two "all apps" dialogs in sync for web-crawler websites.
 */
export function getWebCrawlerDatasources(
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): ThirdPartyConfigsWithAPIResponse[] {
	const config = thirdPartyConfigs[ProductKeysObject.AtlassianWebCrawler];
	if (!config?.fanOutConnectorSources) {
		return [];
	}
	return config.datasources ?? [];
}

/**
 * Splits app options into authenticated (1P + connected 3P) and unauthenticated 3P.
 *
 * Auth is derived via `isProductConnected` — the same derivation the non-lite rail
 * uses to render its "Connect"/OAuth affordance — so Lite pills render their
 * authenticated state identically to non-lite, and the `viewed searchPageFilters`
 * analytics (which also reads `isProductConnected`) matches what is rendered.
 */
export function splitByAuthStatus(
	options: AppOption[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): { authenticatedOptions: AppOption[]; unauthenticatedOptions: AppOption[] } {
	const authenticatedOptions = options.filter((opt) =>
		isProductConnected(opt.value, thirdPartyConfigs),
	);

	const unauthenticatedOptions = options
		.filter((opt) => !isProductConnected(opt.value, thirdPartyConfigs))
		.map((opt) => ({ ...opt, isUnauthenticated: true }));

	return { authenticatedOptions, unauthenticatedOptions };
}
